export type SpriteCue = {
  start: number;
  end: number;
  fileName: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

export type SpriteSheetMap = { [name: string]: string };

export type SpriteFrame = {
  url: string;
  dispW: number;
  dispH: number;
  imgW: number;
  imgH: number;
  offsetX: number;
  offsetY: number;
};

var SPRITE_MAX_WIDTH = 120;

export function normalizeSpriteMap(raw: unknown): SpriteSheetMap {
  var out: SpriteSheetMap = {};
  if (!raw || typeof raw !== "object") return out;
  var rec = raw as { [name: string]: unknown };
  var key: string;
  for (key in rec) {
    if (!Object.prototype.hasOwnProperty.call(rec, key)) continue;
    if (typeof rec[key] === "string" && rec[key]) {
      out[key] = String(rec[key]);
    }
  }
  return out;
}

export function readSpriteFields(opt: { preview?: unknown; sprites?: unknown } | null | undefined): {
  preview: string;
  sprites: SpriteSheetMap;
} {
  var preview = "";
  if (opt && typeof opt.preview === "string") preview = opt.preview;
  return {
    preview: preview,
    sprites: normalizeSpriteMap(opt ? opt.sprites : null),
  };
}

function vttTimestampToSeconds(raw: string): number {
  var trimmed = raw.trim();
  var parts = trimmed.split(":");
  var sec = 0;
  if (parts.length === 3) {
    sec = Number(parts[0]) * 3600 + Number(parts[1]) * 60 + Number(parts[2]);
  } else if (parts.length === 2) {
    sec = Number(parts[0]) * 60 + Number(parts[1]);
  } else {
    sec = Number(parts[0]);
  }
  if (!isFinite(sec) || sec < 0) return 0;
  return sec;
}

function parseXywhPayload(payload: string, start: number, end: number): SpriteCue | null {
  var hash = payload.indexOf("#xywh=");
  if (hash < 0) return null;
  var fileName = payload.slice(0, hash).trim();
  var lastLine = fileName.lastIndexOf("\n");
  if (lastLine >= 0) fileName = fileName.slice(lastLine + 1).trim();
  if (!fileName) return null;
  var nums = payload.slice(hash + 6).split(",");
  if (nums.length < 4) return null;
  var x = Number(nums[0]);
  var y = Number(nums[1]);
  var w = Number(nums[2]);
  var h = Number(nums[3]);
  if (!isFinite(x) || !isFinite(y) || !isFinite(w) || !isFinite(h)) return null;
  if (w <= 0 || h <= 0) return null;
  return {
    start: start,
    end: end,
    fileName: fileName,
    x: x,
    y: y,
    w: w,
    h: h,
  };
}

export function parseSpriteVtt(vtt: string): SpriteCue[] {
  var text = vtt.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  var lines = text.split("\n");
  var cues: SpriteCue[] = [];
  var i = 0;
  while (i < lines.length) {
    var line = lines[i];
    var arrow = line.indexOf("-->");
    if (arrow < 0) {
      i += 1;
      continue;
    }
    var start = vttTimestampToSeconds(line.slice(0, arrow));
    var endToken = line.slice(arrow + 3).replace(/^\s+/, "").split(/\s+/)[0] || "";
    var end = vttTimestampToSeconds(endToken);
    i += 1;
    var payload = "";
    while (i < lines.length && lines[i].trim() !== "") {
      if (payload) payload += "\n";
      payload += lines[i].trim();
      i += 1;
    }
    var cue = parseXywhPayload(payload, start, end);
    if (cue) cues.push(cue);
  }
  return cues;
}

export function findSpriteCue(cues: SpriteCue[], time: number): SpriteCue | null {
  var lo = 0;
  var hi = cues.length - 1;
  while (lo <= hi) {
    var mid = (lo + hi) >> 1;
    var cue = cues[mid];
    if (time < cue.start) {
      hi = mid - 1;
    } else if (time >= cue.end) {
      lo = mid + 1;
    } else {
      return cue;
    }
  }
  return null;
}

export function spriteUrlForCue(cue: SpriteCue, sprites: SpriteSheetMap): string {
  if (sprites[cue.fileName]) return sprites[cue.fileName];
  var slash = cue.fileName.lastIndexOf("/");
  var baseName = slash >= 0 ? cue.fileName.slice(slash + 1) : cue.fileName;
  if (baseName !== cue.fileName && sprites[baseName]) return sprites[baseName];
  return "";
}

export function spriteDisplaySize(
  cellW: number,
  cellH: number,
  sliderWidth: number
): { w: number; h: number; scale: number } {
  var maxW = SPRITE_MAX_WIDTH;
  var barCap = sliderWidth * 0.22;
  if (barCap > 0 && barCap < maxW) maxW = barCap;
  if (cellW <= 0 || cellH <= 0) return { w: 0, h: 0, scale: 1 };
  var scale = maxW / cellW;
  return {
    w: Math.round(cellW * scale),
    h: Math.round(cellH * scale),
    scale: scale,
  };
}

export function proxyVideoUrl(absoluteUrl: string): string {
  return "/api/video?url=" + encodeURIComponent(absoluteUrl);
}

export function fetchSpriteVtt(url: string): Promise<string> {
  return fetch(url, { credentials: "omit" }).then(function (res) {
    if (!res.ok) throw new Error("preview");
    return res.text();
  }).catch(function () {
    return fetch(proxyVideoUrl(url), { credentials: "same-origin" }).then(function (res) {
      if (!res.ok) throw new Error("preview-proxy");
      return res.text();
    });
  });
}
